
import TabItemTemplate from "@/components/templates/TabItemTemplate.vue";
import {mapActions, mapMutations} from "vuex";
import SimpleDialog from "@/components/organisms/SimpleDialog.vue";
import FileUtils from "@/utils/FileUtils";
import TextFieldLike from "@/components/molecules/TextFieldLike.vue";
import TableInline from "@/components/atoms/TableInline.vue";
import ReadonlyCheckbox from "@/components/atoms/ReadonlyCheckbox.vue";
import IconBtns from "@/components/molecules/IconBtns";
import IconBtn from "@/components/molecules/IconBtn.vue";
import PhotoSwipe from "@/components/atoms/PhotoSwipe.vue";
import parseUrl from "url-parse";
import {defineComponent, PropType} from "vue";
import {ContractProfile} from "@/store/modules/hcs/contractProfile/model";

interface ViewContractProfile extends ContractProfile {
  selected?: boolean;
  fileIsImage: boolean;
  fileDownloadUrl: string;
  fileDownloadUrlRaw: string;
}

export default defineComponent({
  name: "DocumentContractProfileTab",
  components: {
    PhotoSwipe,
    IconBtn, IconBtns, ReadonlyCheckbox, TableInline, TextFieldLike, SimpleDialog, TabItemTemplate},
  mixins: [FileUtils],
  data() {
    return {
      createDialog: false as false | {title?: string; file?: File},
      deleteDialog: false as false | Partial<ViewContractProfile>,
      photoSwipe: {
        visible: false,
        items: [] as {
          src: string;
          w: number;
          h: number;
        }[],
      },
      notationType: 'document',
      indeterminateSelected: [] as string[], // 選択ロード中
    };
  },
  props: {
    contractProfileTemplateId: {
      type: [String, null] as PropType<null | string>,
      required: false,
      default: null,
    },
    tradeContractId: {
      type: [String, null] as PropType<null | string>,
      required: false,
      default: null,
    },
    tradeContractFormatId: {
      type: [String, null] as PropType<null | string>,
      required: false,
      default: null,
    },
    contractProfiles: {
      type: Array as PropType<ContractProfile[]>,
      required: true
    },
    readonly: {
      type: Boolean,
      default: false
    },
    hideSelection: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
  },
  emits: [
    'change:contract-profiles'
  ],
  computed: {
    contractProfileView(): ViewContractProfile[] {
      return this.contractProfiles
          .filter(cp => cp.notationType === this.notationType)
          .map(cp => {
            const fileDownloadUrl = parseUrl(cp.fileDownloadUrl, true);
            delete fileDownloadUrl.query['disposition'];
            return {
              ...cp,
              fileIsImage: cp.fileMimeType?.startsWith('image/') ?? false,
              fileDownloadUrl: fileDownloadUrl.toString(),
              fileDownloadUrlRaw: cp.fileDownloadUrl
            };
          });
    },
  },
  methods: {
    ...mapMutations({
      showSnackbar: 'showSnackbar'
    }),
    ...mapActions({
      createContractProfile: 'hcs/contractProfile/create',
      updateContractProfile: 'hcs/contractProfile/update',
      deleteContractProfile: 'hcs/contractProfile/delete',
    }),
    async handleSelectedChange(row: ViewContractProfile, isSelected: boolean) {
      try {
        this.indeterminateSelected.push(row.id);
        await this.updateContractProfile({
          id: row.id,
          selected: isSelected
        });
        row.selected = isSelected;
      } catch(e) {
        console.error(e);
        this.showSnackbar({
          color: 'failed',
          message: `選択の反映に失敗しました`,
        });
      } finally {
        const pos = this.indeterminateSelected.indexOf(row.id);
        if(pos !== -1) {
          this.indeterminateSelected.splice(pos, 1);
        }
      }
    },
    openCreateDialog() {
      this.createDialog = {};
    },
    closeCreateDialog(apply: boolean) {
      if(apply && this.createDialog !== false) {
        this.showSnackbar({
          message: '契約文書を作成中...',
          timeout: false,
          loading: true
        });
        const title = this.createDialog.title as string;
        const file = this.createDialog.file as File;
        new Promise((resolve, reject) => {
          const reader = new FileReader()
          reader.addEventListener('load', e => {
            resolve(e.target?.result);
          });
          reader.addEventListener('error', _ => {
            reject();
          });
          reader.readAsDataURL(file);
        }).then(fileDataUrl => {
          return this.createContractProfile({
            notationType: this.notationType,
            title,
            content: file.name,
            file: fileDataUrl,
            contractProfileTemplateId: this.contractProfileTemplateId,
            tradeContractId: this.tradeContractId,
            tradeContractFormatId: this.tradeContractFormatId,
          });
        }).then(newContactProfile => {
          this.showSnackbar({
            message: '契約文書を作成しました',
          });
          this.contractProfiles.push(newContactProfile);
          this.$emit('change:contract-profiles');
        }, reason => {
          console.error(reason);
          this.showSnackbar({
            color: 'failed',
            message: `契約文書の作成に失敗しました`,
          });
        });
      }
      this.createDialog = false;
    },
    openDeleteDialog(row: ViewContractProfile) {
      this.deleteDialog = row;
    },
    closeDeleteDialog(apply: boolean) {
      if(apply && this.deleteDialog !== false) {
        this.showSnackbar({
          message: '契約文書を削除中...',
          timeout: false,
          loading: true
        });
        this.deleteContractProfile(this.deleteDialog.id).then(contractProfileId => {
          this.showSnackbar({
            message: '契約文書を削除しました',
          });
          const index = this.contractProfiles.findIndex(cp => cp.id === contractProfileId);
          if(index !== -1) {
            this.contractProfiles.splice(index, 1);
            this.$emit('change:contract-profiles');
          }
        }, () => {
          this.showSnackbar({
            color: 'failed',
            message: '契約文書の削除に失敗しました',
          });
        });
      }
      this.deleteDialog = false;
    },
    async showImage(row: ViewContractProfile, event: MouseEvent) {
      const el = event.target as EventTarget & {prevent: boolean; click: () => {}};
      if(event.metaKey || event.ctrlKey || event.shiftKey || el.prevent) {
        return;
      } else {
        event.preventDefault();
      }

      const loading = this.$loading({
        lock: true,
        // text: '読み込み中...',
        spinner: 'el-icon-loading',
      });

      try {
        const img = await this.loadImage(row.fileDownloadUrl);
        this.photoSwipe.visible = true;
        this.photoSwipe.items = [{
          src: row.fileDownloadUrl,
          w: img.width,
          h: img.height,
        }];
      } catch (e) {
        el.prevent = true;
        el.click();
      } finally {
        loading.close();
      }
    }
  }
});
